import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Landmark, Loader2, Lock, RefreshCw, Unlock, Unplug } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const ShareControl = ({ account, candidates, onDone }) => {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(formatApiError(e)); } };
  const free = candidates.filter((c) => !account.shared_with.includes(c.id));
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {account.shared_with.map((uid) => (
        <button key={uid} onClick={() => act(() => api.delete(`/finance/accounts/${account.bridge_id}/share/${uid}`), "Partage retiré")} className="rounded-full bg-card px-2 py-0.5" data-testid={`unshare-${account.bridge_id}-${uid}`}>
          {candidates.find((c) => c.id === uid)?.name || "Membre"} ✕
        </button>
      ))}
      {!!free.length && (
        <select className="rounded-full bg-card px-2 py-0.5" value="" onChange={(e) => e.target.value && act(() => api.post(`/finance/accounts/${account.bridge_id}/share`, { user_id: e.target.value }), "Compte partagé")} data-testid={`share-select-${account.bridge_id}`}>
          <option value="">Partager avec…</option>
          {free.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
    </div>
  );
};

export const BankCard = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const status = useQuery({ queryKey: ["bridge-status"], queryFn: () => api.get("/bridge/status").then((r) => r.data) });
  const accounts = useQuery({ queryKey: ["bank-accounts"], queryFn: () => api.get("/finance/accounts").then((r) => r.data) });
  const txs = useQuery({ queryKey: ["bank-txs"], queryFn: () => api.get("/finance/transactions").then((r) => r.data) });
  const candidates = useQuery({ queryKey: ["share-candidates"], queryFn: () => api.get("/finance/share-candidates").then((r) => r.data) });
  const togglePrivate = async (t) => {
    try {
      await api.put(`/finance/transactions/${t.bridge_id}/private`, { private: !t.private });
      toast.success(t.private ? "Opération de nouveau visible par le foyer" : "Opération masquée aux membres qui voient ce compte");
      ["bank-txs", "planning", "report-ops"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bridge/sync");
      toast.success(`${data.accounts} compte(s) et ${data.transactions} opération(s) synchronisés`);
      ["bridge-status", "bank-accounts", "bank-txs"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  useEffect(() => {
    if (params.get("bridge") === "callback") {
      setParams({}, { replace: true });
      if (params.get("success") === "false") toast.error("Connexion bancaire interrompue");
      else sync();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.post("/bridge/connect"); window.location.assign(data.url); } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  const s = status.data;
  const refreshAll = () => ["bridge-status", "bank-accounts", "bank-txs", "expense-summary"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const simulate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bridge/simulate");
      toast.success(`Simulation : ${data.accounts} compte(s) et ${data.transactions} opération(s) fictives`);
      refreshAll();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const stopSim = async () => {
    try { await api.delete("/bridge/simulate"); toast.success("Simulation déconnectée"); refreshAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="card-surface space-y-4 p-6" data-testid="bank-card">
      <div className="flex flex-wrap items-center gap-3">
        <span className="tone-chip tone-butter flex h-9 w-9 items-center justify-center rounded-xl"><Landmark className="h-4 w-4" /></span>
        <div className="flex-1">
          <h2 className="text-lg font-bold">Mes comptes bancaires</h2>
          <p className="text-xs text-muted-foreground">Connexion sécurisée en lecture seule via un agrégateur agréé ACPR : aucun virement possible, vos identifiants bancaires ne transitent jamais par CASHMY.</p>
        </div>
        {s?.configured && !user.is_demo && (
          <div className="flex gap-2">
            {s.linked && <Button variant="outline" className="rounded-full" onClick={sync} disabled={busy} data-testid="bank-sync-button"><RefreshCw className={`mr-2 h-4 w-4 ${busy ? "animate-spin" : ""}`} />Synchroniser</Button>}
            <Button className="rounded-full" onClick={connect} disabled={busy} data-testid="bank-connect-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Connecter une banque</Button>
          </div>
        )}
      </div>
      {s && (!s.configured || (!user.is_demo && (s.simulated || !s.linked))) && (
        <div className="flex flex-col gap-3 rounded-xl bg-secondary p-3 text-sm sm:flex-row sm:items-center" data-testid="bank-not-configured">
          <FlaskConical className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
          <p className="min-w-0 flex-1 text-muted-foreground">
            {s.simulated ? <><span className="mr-2 rounded-full bg-card px-2 py-0.5 text-xs font-bold" data-testid="bank-simulation-badge">SIMULATION</span>Banque fictive connectée en attendant l'activation de la connexion bancaire. Aucune donnée réelle.</>
              : s.configured ? "Envie d'essayer d'abord ? Connectez une banque de démonstration avec des comptes et des mouvements fictifs, déconnectable à tout moment."
              : "La connexion bancaire réelle sera bientôt activée. En attendant, vous pouvez simuler une connexion avec une banque fictive."}
          </p>
          <div className="flex flex-wrap gap-2">
            {s.simulated && <Button variant="outline" size="sm" className="rounded-full" onClick={sync} disabled={busy} data-testid="bank-sync-button"><RefreshCw className={`mr-2 h-4 w-4 ${busy ? "animate-spin" : ""}`} />Synchroniser</Button>}
            {s.simulated ? (!user.is_demo && <Button variant="ghost" size="sm" className="rounded-full" onClick={stopSim} data-testid="bank-simulate-stop-button"><Unplug className="mr-2 h-4 w-4" />Déconnecter</Button>)
              : <Button size="sm" className="rounded-full" onClick={simulate} disabled={busy} data-testid="bank-simulate-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{s.configured ? "Banque de démonstration" : "Simuler une connexion"}</Button>}
          </div>
        </div>
      )}
      {s?.configured && user.is_demo && <p className="rounded-xl bg-secondary p-3 text-sm text-muted-foreground" data-testid="bank-demo-disabled">Indisponible sur les comptes de démonstration : créez votre propre compte pour connecter votre banque.</p>}
      {s?.last_sync_at && <p className="text-xs text-muted-foreground">Dernière synchronisation : {new Date(s.last_sync_at).toLocaleString("fr-FR")}</p>}
      {!!accounts.data?.length && (
        <ul className="grid gap-2 sm:grid-cols-2" data-testid="bank-accounts-list">
          {accounts.data.map((a) => (
            <li key={`${a.owner_name}-${a.bridge_id}`} className="tone-tile tone-sky min-w-0 space-y-2 rounded-xl p-3 text-sm" data-testid={`bank-account-${a.bridge_id}`}>
              <div className="flex min-w-0 justify-between gap-2"><span className="min-w-0 truncate">{a.name}</span><span className="num shrink-0 font-bold">{money(a.balance, a.currency || "EUR", "fr-FR", { maximumFractionDigits: 2 })}</span></div>
              {a.is_mine ? <ShareControl account={a} candidates={candidates.data || []} onDone={() => qc.invalidateQueries({ queryKey: ["bank-accounts"] })} />
                : <div className="text-xs text-muted-foreground">Partagé par {a.owner_name} (lecture seule)</div>}
            </li>
          ))}
        </ul>
      )}
      {!!txs.data?.length && (
        <ul className="divide-y divide-border" data-testid="bank-transactions-list">
          {txs.data.slice(0, 15).map((t) => (
            <li key={`${t.account_id}-${t.bridge_id}`} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`bank-tx-${t.bridge_id}`}>
              <span className="min-w-0 flex-1"><span className={`flex items-center gap-1 truncate ${t.amount === null ? "italic text-muted-foreground" : ""}`}>{t.amount === null && <Lock className="h-3.5 w-3.5" />}{t.label}</span><span className="text-xs text-muted-foreground">{new Date(t.date).toLocaleDateString("fr-FR")}</span></span>
              <span className={`num shrink-0 ${t.amount < 0 ? "" : "text-success"}`}>{t.amount === null ? "•••" : money(t.amount, t.currency || "EUR", "fr-FR", { maximumFractionDigits: 2 })}</span>
              {t.mine && <button type="button" onClick={() => togglePrivate(t)} title={t.private ? "Visible uniquement par vous — cliquer pour la montrer au foyer" : "Masquer aux membres du foyer"} aria-label="Masquer l'opération" className={`rounded-full p-1.5 ${t.private ? "bg-warning text-white" : "text-muted-foreground hover:bg-muted"}`} data-testid={`bank-tx-private-${t.bridge_id}`}>{t.private ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}</button>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
